import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Home/Components/footer/Footer";
import "./Blogs.css";

import { API_BASE_URL } from "../../config";

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [activeAnchor, setActiveAnchor] = useState("");

    // Refs for JS-driven sticky sidebar
    const sidebarRef = useRef(null);
    const layoutRef = useRef(null);
    const [sidebarStyle, setSidebarStyle] = useState({});

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
        fetchBlogDetail();
    }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchBlogDetail = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("admin_token");
            const headers = {};
            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/api/blogs/${slug}`, { headers });
            if (response.status === 404) {
                throw new Error("This article could not be found.");
            }
            if (response.status === 403) {
                throw new Error("This article is a draft or scheduled for future publication.");
            }
            if (!response.ok) {
                throw new Error("Could not load article details.");
            }
            const data = await response.json();
            setBlog(data);
        } catch (err) {
            setError(err.message || "Failed to load blog.");
        } finally {
            setLoading(false);
        }
    };

    // JS-driven sticky: fixes the sidebar to the viewport once it scrolls past its natural position
    useEffect(() => {
        if (!blog) return;

        const OFFSET_TOP = 30; // px from viewport top when fixed

        const handleScroll = () => {
            const layout = layoutRef.current;
            const sidebar = sidebarRef.current;
            if (!layout || !sidebar) return;

            const layoutRect = layout.getBoundingClientRect();
            const sidebarNaturalTop = layout.offsetTop; // top of the grid relative to document
            const sidebarHeight = sidebar.offsetHeight;
            const sidebarWidth = sidebar.offsetWidth;

            const scrollY = window.scrollY;

            // When the layout top has scrolled past the viewport top (minus offset)
            if (scrollY > sidebarNaturalTop - OFFSET_TOP) {
                // Make sure we don't go past the bottom of the layout
                const layoutBottom = layoutRect.bottom + scrollY; // absolute bottom of layout
                const fixedBottom = scrollY + OFFSET_TOP + sidebarHeight;

                if (fixedBottom >= layoutBottom) {
                    // Pin to bottom of layout (absolute positioning)
                    setSidebarStyle({
                        position: "absolute",
                        top: layout.offsetHeight - sidebarHeight + "px",
                        left: 0,
                        width: sidebarWidth + "px",
                    });
                } else {
                    // Fix to viewport
                    setSidebarStyle({
                        position: "fixed",
                        top: OFFSET_TOP + "px",
                        left: layoutRect.left + "px",
                        width: sidebarWidth + "px",
                        zIndex: 20,
                    });
                }
            } else {
                // Back to normal flow
                setSidebarStyle({});
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        // Run once on mount in case page is already scrolled
        handleScroll();

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [blog]);

    // IntersectionObserver for active anchor highlighting
    useEffect(() => {
        if (!blog || !blog.blocks) return;

        const anchors = blog.blocks.filter(b => b.type === "heading" && b.isTimelineAnchor && b.anchorId);
        if (anchors.length === 0) return;

        setActiveAnchor(anchors[0].anchorId);

        const observerOptions = {
            root: null,
            rootMargin: "-120px 0px -70% 0px",
            threshold: 0,
        };

        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveAnchor(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);
        anchors.forEach((a) => {
            const el = document.getElementById(a.anchorId);
            if (el) observer.observe(el);
        });

        return () => {
            observer.disconnect();
        };
    }, [blog]);

    const scrollToAnchor = (anchorId) => {
        const element = document.getElementById(anchorId);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
            setActiveAnchor(anchorId);
        }
    };

    const formatDate = (dateString) => {
        const options = { year: "numeric", month: "long", day: "numeric" };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <div style={{ textAlign: "center", padding: "120px 20px", fontSize: "20px", color: "#555" }}>
                    Loading story content...
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Navbar />
                <div style={{ textAlign: "center", padding: "120px 20px" }}>
                    <h3 style={{ color: "#ef4444", marginBottom: "15px" }}>Error</h3>
                    <p style={{ color: "#777", marginBottom: "25px" }}>{error}</p>
                    <button className="btn-secondary" onClick={() => navigate("/blogs")}>Back to Blogs</button>
                </div>
                <Footer />
            </>
        );
    }

    const timelineAnchors = blog.blocks.filter(b => b.type === "heading" && b.isTimelineAnchor && b.anchorId);

    return (
        <>
            <Navbar />

            <div className="blog-detail-wrapper">
                {blog.coverImage && (
                    <div className="blog-detail-banner">
                        <img src={blog.coverImage} alt={blog.title} />
                    </div>
                )}

                <div className="blog-post-header" style={{ marginTop: blog.coverImage ? "0px" : "80px" }}>
                    <div className="blog-meta-info">{formatDate(blog.createdAt)}</div>
                    <h1 style={{ fontFamily: "DM Serif Display", marginTop: "10px" }}>{blog.title}</h1>
                </div>

                {/* layout ref wraps the two-column grid so JS can measure offsets */}
                <div className="blog-detail-layout" ref={layoutRef} style={{ position: "relative" }}>

                    {/* Left column: placeholder keeps the grid column width intact */}
                    <div className="blog-timeline-column">
                        {/* Invisible placeholder so the right column doesn't collapse */}
                        <div style={{ visibility: "hidden", pointerEvents: "none" }} aria-hidden="true">
                            {timelineAnchors.map((anchor) => (
                                <div key={anchor.id} style={{ padding: "8px 0", fontSize: "15px" }}>
                                    {anchor.anchorLabel || anchor.content}
                                </div>
                            ))}
                        </div>

                        {/* Actual floating sidebar driven by JS */}
                        <div
                            className="blog-timeline-panel"
                            ref={sidebarRef}
                            style={sidebarStyle}
                        >
                            {timelineAnchors.length > 0 ? (
                                <ul className="timeline-navigation-list">
                                    {timelineAnchors.map((anchor) => (
                                        <li
                                            key={anchor.id}
                                            className={`timeline-nav-item ${activeAnchor === anchor.anchorId ? "active" : ""}`}
                                            onClick={() => scrollToAnchor(anchor.anchorId)}
                                        >
                                            {anchor.anchorLabel || anchor.content}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p style={{ color: "#aaa", fontSize: "14px", fontStyle: "italic", paddingLeft: "10px" }}>Story Overview</p>
                            )}
                        </div>
                    </div>

                    {/* Right panel - Main Blog Content */}
                    <div className="blog-main-content">
                        {blog.blocks && blog.blocks.map((block) => {
                            switch (block.type) {
                                case "heading":
                                    return (
                                        <h2
                                            key={block.id}
                                            id={block.isTimelineAnchor ? block.anchorId : undefined}
                                            className="blog-block-heading"
                                        >
                                            {block.content}
                                        </h2>
                                    );
                                case "subheading":
                                    return (
                                        <h3 key={block.id} className="blog-block-subheading">
                                            {block.content}
                                        </h3>
                                    );
                                case "paragraph":
                                    return (
                                        <p key={block.id} className="blog-block-paragraph">
                                            {block.content}
                                        </p>
                                    );
                                case "list":
                                    return (
                                        block.listType === "ordered" ? (
                                            <ol key={block.id} className="blog-block-list">
                                                {block.items.map((item, idx) => (
                                                    <li key={idx}>{item}</li>
                                                ))}
                                            </ol>
                                        ) : (
                                            <ul key={block.id} className="blog-block-list">
                                                {block.items.map((item, idx) => (
                                                    <li key={idx}>{item}</li>
                                                ))}
                                            </ul>
                                        )
                                    );
                                case "table":
                                    return (
                                        <div key={block.id} className="blog-block-table-container">
                                            <table className="blog-block-table">
                                                <thead>
                                                    <tr>
                                                        {block.headers.map((hdr, idx) => (
                                                            <th key={idx}>{hdr}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {block.rows.map((row, rowIdx) => (
                                                        <tr key={rowIdx}>
                                                            {row.map((cell, cellIdx) => (
                                                                <td key={cellIdx}>{cell}</td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                case "image":
                                    return (
                                        <div key={block.id} className="blog-block-image-container">
                                            {block.url && <img src={block.url} alt={block.caption || "Blog illustration"} />}
                                            {block.caption && <div className="blog-block-image-caption">{block.caption}</div>}
                                        </div>
                                    );
                                case "timeline":
                                    return (
                                        <div key={block.id} className="blog-timeline-element">
                                            <div className="blog-timeline-node">
                                                <div className="blog-timeline-badge">{block.year}</div>
                                                <div className="blog-timeline-line"></div>
                                            </div>
                                            <div className="blog-timeline-info">
                                                <h4>{block.milestoneTitle}</h4>
                                                <p>{block.milestoneDesc}</p>
                                            </div>
                                        </div>
                                    );
                                default:
                                    return null;
                            }
                        })}
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default BlogDetail;
